import { cn } from '@/lib/utils'
import type { CallCountItem, ConnectivityItem } from '@/hooks/useRealtime'

interface SummaryStatsCardProps {
  summaryTotalCalls?: CallCountItem[]
  summarySuccessfulCalls?: CallCountItem[]
  summaryConnectedCalls?: ConnectivityItem[]
  summaryCallsOver30s?: CallCountItem[]
}

export function SummaryStatsCard({
  summaryTotalCalls,
  summarySuccessfulCalls,
  summaryConnectedCalls,
  summaryCallsOver30s,
}: SummaryStatsCardProps) {
  if (!summaryTotalCalls || !summarySuccessfulCalls || !summaryConnectedCalls || !summaryCallsOver30s) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
        <h2 className="text-heading font-bold mb-4">Summary Statistics</h2>
        <div className="text-center text-muted-foreground text-body py-4">
          No real-time data available yet. Metrics will appear once calls start.
        </div>
      </div>
    )
  }

  // Calculate summary statistics
  const totalCallsSum = summaryTotalCalls.reduce((acc, item) => acc + Number(item.calls), 0)
  const successfulCallsSum = summarySuccessfulCalls.reduce((acc, item) => acc + Number(item.calls), 0)
  const callsOver30sSum = summaryCallsOver30s.reduce((acc, item) => acc + Number(item.calls), 0)

  const successRate = totalCallsSum > 0 ? (successfulCallsSum / totalCallsSum) * 100 : 0
  // Average connectivity rate from the intervals
  const connectivityRate =
    summaryConnectedCalls.length > 0
      ? summaryConnectedCalls.reduce((acc, item) => acc + Number(item.connectivity_rate), 0) /
        summaryConnectedCalls.length
      : 0
  const shareOver30s = totalCallsSum > 0 ? (callsOver30sSum / totalCallsSum) * 100 : 0

  const kpis = [
    {
      label: 'Total Calls (2h)',
      value: totalCallsSum.toLocaleString(),
      subValue: 'Last 2 hours',
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
    },
    {
      label: 'Success Rate',
      value: `${successRate.toFixed(2)}%`,
      subValue: `${successfulCallsSum.toLocaleString()} successful calls`,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: 'Connectivity Rate',
      value: `${connectivityRate.toFixed(2)}%`,
      subValue: 'Average rate',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Calls Over 30s',
      value: `${shareOver30s.toFixed(2)}%`,
      subValue: `${callsOver30sSum.toLocaleString()} calls`,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
  ]

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
      <h2 className="text-heading font-bold mb-4">Summary Statistics</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <div key={index} className={cn('rounded-lg p-4', kpi.bgColor)}>
            <div className="text-body font-medium text-muted-foreground mb-1">{kpi.label}</div>
            <div className={cn('text-heading font-bold mb-1', kpi.color)}>{kpi.value}</div>
            <div className="text-detail text-muted-foreground">{kpi.subValue}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
